import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { toast } from "react-toastify";
import { layoutCSS } from "../component/common/commonStyle";
import {
  useCreateWhatsappChatAgentMutation,
  useGetPhoneNUmberQuery,
  useGetWhatsappChatAgentsQuery,
  useDeleteWhatsappChatAgentMutation,
} from "../store/services/whatsappApi.js";
import { jwtDecode } from "jwt-decode";

// Bulk input field styles
const inputSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.9)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

const selectSx = {
  background: "rgba(255,255,255,0.9)",
  "& .Mui-disabled": {
    color: "#334155 !important",
    WebkitTextFillColor: "#334155 !important",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
};

export default function CreateAgentForm() {
  const [formData, setFormData] = useState({
    agentName: "",
    email: "",
    department: "Sales",
    designation: "",
  });

  const [selectedPhno, setSelectedPhno] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [createWhatsappChatAgent] = useCreateWhatsappChatAgentMutation();
  const [deleteWhatsappChatAgent] = useDeleteWhatsappChatAgentMutation();

  const { data: phoneData } = useGetPhoneNUmberQuery(customerId, {
    skip: !customerId,
  });

  const { data: agentsList } = useGetWhatsappChatAgentsQuery(customerId, {
    skip: !customerId,
  });

  const departments = useMemo(
    () => ["Sales", "Support", "Marketing", "Operations", "Management", "Finance", "HR"],
    []
  );

  /* =====================
     Decode Token
  ====================== */
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Access token not found");
        return;
      }

      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid;

      if (!id) {
        toast.error("Customer ID not found in token");
        return;
      }

      setCustomerId(id);
    } catch (err) {
      console.error(err);
      toast.error("Invalid access token");
    }
  }, []);

  /* =====================
     Handlers
  ====================== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.agentName.trim()) {
      setError("Agent Name is required");
      return false;
    }

    if (!formData.email.trim()) {
      setError("Email is required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!selectedPhno) {
      setError("Phone number is required");
      return false;
    }

    return true;
  };

  /* =====================
     SUBMIT
  ====================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        customer_id: customerId,
        wabanumberid: selectedPhno,
        agents: [
          {
            name: formData.agentName,
            email: formData.email,
            is_active: true,
          },
        ],
      };

      await createWhatsappChatAgent(payload).unwrap();

      toast.success("Agent created successfully!");
      handleReset();
    } catch (err) {
      console.error(err);
      const message =
        err?.data?.message || err?.error || "Failed to create agent";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      agentName: "",
      email: "",
      department: "Sales",
      designation: "",
    });
    setSelectedPhno("");
    setError("");
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm("Are you sure you want to delete this agent?")) return;

    try {
      await deleteWhatsappChatAgent({
        customer_id: customerId,
        agent_id: agentId,
      }).unwrap();
      toast.success("Agent deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.message || "Failed to delete agent");
    }
  };

  /* =====================
     UI
  ====================== */
  return (
    <Box sx={layoutCSS}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* CREATE AGENT FORM - LEFT SIDE */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                height: { xs: "auto", md: "65vh" },
                overflowY: "auto",
              }}
            >
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <PersonIcon sx={{ fontSize: 28, color: "#1976d2" }} />
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Create New Agent
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add a new agent to your team
                  </Typography>
                </Box>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Agent Name"
                      name="agentName"
                      value={formData.agentName}
                      onChange={handleChange}
                      size="small"
                      required
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      size="small"
                      required
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" required sx={inputSx}>
                      <InputLabel>Phone Number</InputLabel>
                      <Select
                        label="Phone Number"
                        value={selectedPhno}
                        onChange={(e) => setSelectedPhno(e.target.value)}
                        sx={selectSx}
                      >
                        {phoneData?.phone_numbers?.map((p) => (
                          <MenuItem key={p.id} value={p.phone_number_id}>
                            {p.phone_number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    mt: 3,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<CancelIcon />}
                    disabled={loading}
                  >
                    Reset
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? "Creating..." : "Create Agent"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* AGENTS LIST TABLE - RIGHT SIDE */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                height: { xs: "auto", md: "65vh" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Active Agents
              </Typography>

              <TableContainer
                sx={{
                  flexGrow: 1,
                  maxHeight: 400,
                  overflowY: "auto",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          backgroundColor: "#f5f5f5",
                          fontWeight: "bold",
                        },
                      }}
                    >
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {agentsList && agentsList.length > 0 ? (
                      agentsList.map((agent) => (
                        <TableRow key={agent.agent_id} hover>
                          <TableCell>{agent.name}</TableCell>
                          <TableCell>{agent.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={agent.is_active ? "Active" : "Inactive"}
                              color={agent.is_active ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteAgent(agent.agent_id)}
                              title="Delete Agent"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2 }}
                          >
                            No agents found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}